/**
 * RESPONSIBILITY: one sermon, as the pastor enters it.
 *
 * The video lives on YouTube and is pasted as a link; its picture comes from there too,
 * so adding last Sunday's sermon is a link, a date, a series and a speaker. Everything
 * the pastor writes afterwards — the description, the notes, the questions for small
 * groups — is on the sermon's own page, and every part of it may be left for later (R31).
 *
 * Series, speaker and special event are each CHOSEN from a list kept on the Sermons
 * section, never typed (R69): a typed series is how "Ephesians" and "Ephesians " become
 * two filters. The write path refuses a value that is not on its list.
 */
import type { ImageValue } from '@framework/core';
import {
  Row, Text, Paragraph, Img, Link, Bool, Choice, DateField, RenderOf,
} from '@framework/core/decorators';

const SAYS = 'What does it say?';
const FILED = 'Where is it filed?';

@Row()
export class SermonRow {
  @Text({ label: 'Title', maxLength: 300 })
  title!: string;

  @Link({
    label: 'YouTube link', description: 'The sermon on YouTube. Its picture is taken from there.',
  })
  youtubeUrl!: string;

  @DateField({ label: 'Preached on' })
  preachedOn!: string;

  @Choice({
    label: 'Series', in: 'series',
    description: 'Chosen from the Series list, kept with all the sermons. Empty for a stand-alone sermon.',
  })
  series!: string;

  @Choice({ label: 'Speaker', in: 'speakers', description: 'Chosen from the Speakers list, kept with all the sermons.' })
  speaker!: string;

  @Text({ label: 'Key scripture', maxLength: 300, description: 'e.g. Ephesians 2:1–10.' })
  scripture!: string;

  @Paragraph({
    label: 'Description', maxLength: 20000, rows: 4, markdown: true, allow: ['verse'],
    group: SAYS, collapsed: true,
    description: 'A few lines about the sermon. The first lines show on its card.',
  })
  summary!: string;

  @RenderOf('summary')
  summaryHtml!: string;

  @Paragraph({
    label: 'Sermon notes', maxLength: 50000, rows: 10, markdown: true, allow: ['verse'],
    group: SAYS, description: 'The notes shared after the sermon. Headings, lists and verses all work.',
  })
  notes!: string;

  @RenderOf('notes')
  notesHtml!: string;

  @Paragraph({
    label: 'Application and discussion questions', maxLength: 20000, rows: 6, markdown: true,
    allow: ['verse'], group: SAYS,
    description: 'For personal reflection or a small group. A numbered list reads best.',
  })
  questions!: string;

  @RenderOf('questions')
  questionsHtml!: string;

  @Choice({
    label: 'Special event', in: 'occasions', group: FILED, collapsed: true,
    description: 'Only when it was preached at one — e.g. Easter, the 25th Anniversary.',
  })
  occasion!: string;

  @Bool({
    label: 'Show this sermon', default: true, group: FILED,
    description: 'Untick to keep it off the site while it is being written. You still see it, marked.',
  })
  show!: boolean;

  @Img({
    label: 'Picture', folder: 'sermons', maxSizeMb: 12, size: '1280 × 720', group: FILED,
    description: 'Optional. Empty uses the picture from YouTube.',
  })
  image!: ImageValue;

  @Text({
    label: 'Web address', maxLength: 300, group: FILED,
    description: 'The last part of this sermon’s link. Leave empty to use the title.',
  })
  slug!: string;

  /** The address, made unique by the server from the preference in `slug`. */
  @Text({ label: 'Web address', derived: true })
  url!: string;
}
